<template>
    <div class="modl-tab-body">
        <div class="modl-body">
            <OfflineNotification message="SEARCH_MODAL.OFFLINE_ERROR" />
            <CheckClubcardTokenForm @loaded="onTokenLoaded" />

            <div v-if="token && token.redeemable && items"
                 class="mt-2">
                <h4 class="text-center">
                    {{ l('SEARCH_MODAL.ITEMS_HEADING') }}
                </h4>
                <div v-if="items.length > 0"
                     class="voucher-items">
                    <VoucherItem v-for="item in items"
                                 :key="item.ID"
                                 :item="item"
                                 @click="addItemToCart" />
                </div>
            </div>
        </div>

        <div class="modl-footer">
            <!-- Cancel button -->
            <button class="btn btn-lg btn-default cancel-btn"
                    @click.prevent="$emit('cancel')">
                {{ l('CANCEL') }}
            </button>
        </div>
    </div>
</template>

<script>
import CheckClubcardTokenForm from '@/epos/vue/modals/Search/CheckClubcardTokenForm';
import OfflineNotification from '@/epos/vue/components/Generic/OfflineNotification';
import VoucherItem from '@/epos/vue/modals/Search/VoucherItem';
import { container } from '@/container';
import { sortByNavOrder } from '@/functions/arraySorting';

export default {
    name: 'CheckClubcardTokenTab',

    components: {
        VoucherItem,
        CheckClubcardTokenForm,
        OfflineNotification,
    },

    emits: [
        'cancel',
        'resolve',
    ],

    data() {
        return {
            /**
             * @type {?ClubcardToken}
             */
            token: null,

            /**
             * @type {PlaceholderItem[]}
             */
            items: [],
        };
    },

    methods: {
        /**
         * @param {ClubcardTokenWithItems|null} tokenWithItems
         */
        onTokenLoaded(tokenWithItems) {
            this.token = tokenWithItems ? tokenWithItems.token : null;
            this.setItems(tokenWithItems ? tokenWithItems.items : [])
                .then(() => {
                });
        },

        /**
         * Sets the list of items to display in the modal.
         * This will create a PlaceholderItem for each item returned by the Clubcard Service.
         *
         * @param {PlaceholderItem[]} placeholderItems
         */
        async setItems(placeholderItems) {
            if (!placeholderItems) {
                this.items = [];
                return;
            }

            // For each item that was returned from the Clubcard Service try to find a matching item in IndexedDB.
            // But there's a catch - the item might only be available in a different branch to the currently selected
            // item branch.
            // That other branch may or may not have had its items synced yet (it depends on if the user has selected
            // that branch in the dropdown yet). So items for other branches might be there already or they might not
            // be there yet. To prevent overcomplicating this we grey out all items that don't exist in the currently
            // selected item branch regardless of if they are in IndexedDB or not.
            // It's also possible a token is valid only for items in branches that the current user does not have
            // access to. In this case those items will also be greyed out.
            await Promise.all(placeholderItems.map(async (placeholderItem) => {
                let item;
                try {
                    item = await container.itemRepository.getItem(placeholderItem.itemType, placeholderItem.ID);
                } catch (e) {
                    console.warn('Failed to load item.', placeholderItem.itemType, placeholderItem.ID, e);
                }

                // Only show items available in the current branch.
                // If we didn't do this, items would be shown if they're in the current branch or if they're in
                // another branch that has been selected since the last sync. That's too awkward to explain so lets
                // keep it simple.
                if (item && !item.branchIDs.includes(container.userService.getItemBranchID())) {
                    item = null;
                }

                if (item) {
                    placeholderItem.setItem(item);
                }
            }));

            sortByNavOrder(placeholderItems);
            this.items = placeholderItems;
        },

        /**
         * @param {AbstractItem} item
         */
        async addItemToCart(item) {
            if (await container.cartService.addItemToCartAndGoToSell(item)) {
                this.$emit('resolve');
            }
        },
    },
};
</script>

<style lang="less">
@import "../../../../less/variables";

.voucher-items {
    display: grid;
    grid-gap: @gutter-width;
    grid-template-columns: repeat(3, 1fr);
    grid-auto-rows: 80px;
}
</style>
